import { createSelector } from '@reduxjs/toolkit';
import { createCachedSelector } from 're-reselect';
import { constants } from '@config';

// Source: https://math.stackexchange.com/questions/270194/how-to-find-the-vertices-angle-after-rotation
// TODO: Move to utils folder AND make unit tests
function getRotatedCoordinates (x, y, centerX, centerY, rotation) {
  const rotationInRadians = Number(rotation) * Math.PI / 180;
  return [
    (x - centerX) * Math.cos(rotationInRadians) - (y - centerY) * Math.sin(rotationInRadians) + centerX,
    (x - centerX) * Math.sin(rotationInRadians) + (y - centerY) * Math.cos(rotationInRadians) + centerY
  ];
}

function calculateButtonsCoordinates (element) {
  const topLeftX1 = element.page.left + element.x + element.width;
  const topLeftY1 = element.page.top + element.y + element.height;
  const centerX1 = element.page.left + element.x + element.width / 2;
  const centerY1 = element.page.top + element.y + element.height / 2;
  const [x1, y1] = getRotatedCoordinates(
    topLeftX1, topLeftY1 - constants.text.textOverflowButtonOffset, centerX1, centerY1, element.rotation);

  const bottomRightX2 = element.next.page.left + element.next.x;
  const bottomRightY2 = element.next.page.top + element.next.y;
  const centerX2 = element.next.page.left + element.next.x + element.next.width / 2;
  const centerY2 = element.next.page.top + element.next.y + element.next.height / 2;
  const [x2, y2] = getRotatedCoordinates(
    bottomRightX2, bottomRightY2 + constants.text.textOverflowButtonOffset, centerX2, centerY2, element.next.rotation);

  return {
    key: `line-${element.pageId}-${element.id}-${element.next.pageId}-${element.next.id}`, x1, y1, x2, y2
  };
}

export const makeGetExtendedPages = () => {
  const extendPage = (pageData, previousPageData, relativeWidth, pagePadding) => {
    const ratio = relativeWidth / pageData.width;
    const width = relativeWidth;
    const height = pageData.height * ratio;
    const top = previousPageData
      ? previousPageData.top + previousPageData.height + (pagePadding * 2)
      : pagePadding;
    const left = pagePadding;

    return { ...pageData, ratio, width, height, top, left };
  };

  const getExtendedPages = createCachedSelector(
    (state) => state.editor.pagesOrder,
    (state) => state.page,
    (state, relativeWidth, pagePadding) => relativeWidth,
    (state, relativeWidth, pagePadding) => pagePadding,
    (pagesOrder, pages, relativeWidth, pagePadding) =>
      pagesOrder.reduce((acc, id, index) => {
        const previousPageData = index > 0 ? acc[index - 1] : null;
        return [
          ...acc,
          extendPage(pages[id], previousPageData, relativeWidth, pagePadding)
        ];
      }, [])
  )((state, relativeWidth, pagePadding) => `${relativeWidth}-${pagePadding}`);

  return getExtendedPages;
};

export const makeGetExtendedElements = () => {
  const extendElement = (element, page, textBoxPadding = constants.text.textBoxPadding) => {
    const x = element.x * page.ratio - textBoxPadding;
    const y = element.y * page.ratio - textBoxPadding;
    const width = element.width * page.ratio + (textBoxPadding * 2);
    const height = element.height * page.ratio + (textBoxPadding * 2);

    return { ...element, x, y, width, height, page: { left: page.left, top: page.top }, prev: undefined, next: undefined };
  };

  const extendElementWithPagesReifier = createSelector(
    (state, pages) => pages,
    (pages) =>
      (element) => {
        if (!pages) return element;

        const page = pages.find(({ id }) => id === element.pageId);
        if (!page) return element;

        return extendElement(element, page);
      }
  );

  const extendElementReifier = createSelector(
    (state) => state.editor.linkedTextElements,
    extendElementWithPagesReifier,
    (chains, extendElementWithPages) =>
      (acc, element, elements) => {
        const extendedElement = extendElementWithPages(element);

        for (const chain of chains) {
          const index = chain.findIndex(item => item.pageId === Number(extendedElement.pageId) && item.id === extendedElement.id);
          if (index === -1) continue;

          const prevId = chain[index - 1];
          const nextId = chain[index + 1];
          const prevElement = prevId && elements.find(element => element.id === prevId.id && element.pageId === prevId.pageId);
          const nextElement = nextId && elements.find(element => element.id === nextId.id && element.pageId === nextId.pageId);
          extendedElement.prev = prevElement && extendElementWithPages(prevElement);
          extendedElement.next = nextElement && extendElementWithPages(nextElement);

          extendedElement.lineToNext = extendedElement.next && calculateButtonsCoordinates(extendedElement);
        }

        return [...acc, extendedElement];
      }
  );

  const getExtendedElements = createSelector(
    (state) => state.elements,
    extendElementReifier,
    (elements, extendElement) => {
      const allElements = Object.entries(elements).reduce((acc, [pageId, elements]) => (
        acc.concat(Object.values(elements).map(element => ({ ...element, pageId: Number(pageId) })))
      ), []);

      return allElements.reduce((acc, element) => extendElement(acc, element, allElements), []);
    }
  );

  return getExtendedElements;
};

export const makeGetExtendedElementsWithPageId = () => {
  const getExtendedElementsWithPageId = createCachedSelector(
    (state, pageId) => pageId,
    (state, pageId, elements) => elements,
    (pageId, elements) =>
      elements
        .filter((element) => element.pageId === pageId)
  )((state, pageId) => pageId);

  return getExtendedElementsWithPageId;
};

export const makeGetLinkedTextElementChains = () => {
  const getLinkedTextElementChains = createSelector(
    (state, elements) => elements,
    (elements) => elements.filter(({ lineToNext }) => lineToNext)
  );

  return getLinkedTextElementChains;
};

export const makeGetFlatLinkedTextElements = () => {
  const getFlatLinkedTextElements = createSelector(
    (state) => state.editor.linkedTextElements,
    linkedTextElements =>
      linkedTextElements.reduce((chains, chain) => [
        ...chains,
        ...chain.map((element, index) => {
          const { id, pageId } = element;
          const prevId = chain[index - 1];
          const nextId = chain[index + 1];

          return {
            id,
            pageId,
            prevId,
            nextId
          };
        })
      ], [])
  );

  return getFlatLinkedTextElements;
};

const getExtendedPages = makeGetExtendedPages();
const getExtendedElements = makeGetExtendedElements();
const getExtendedElementsWithPageId = makeGetExtendedElementsWithPageId();
const getLinkedTextElementChains = makeGetLinkedTextElementChains();
const getFlatLinkedTextElements = makeGetFlatLinkedTextElements();

// TODO: [Redux] Move Active Page Index to the  Store
const getActivePageIndex = createSelector(
  (state) => state.editor,
  ({ pagesOrder, activePage }) => pagesOrder.findIndex((pageId) => pageId === activePage)
);

export default {
  getActivePageIndex,
  getExtendedPages,
  getExtendedElements,
  getExtendedElementsWithPageId,
  getLinkedTextElementChains,
  getFlatLinkedTextElements
};
